import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { ApiError, isMockMode } from '@/api';
import {
  CONSENT_REQUIRED_MARKER,
  enterQueue,
  listArtifacts,
  uploadArtifact,
  type ArtifactMeta,
} from '@/api/artifacts';
import { docTypeLabel, listDocumentRequirements } from '@/api/documents';
import { useCaptureCamera, type CaptureFacing } from '@/media/useCaptureCamera';
import { useCaptureIntegrity } from '@/media/useCaptureIntegrity';
import { assessCaptureQuality, type QualityVerdict } from '@/media/imageQuality';
import { useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import type { StepProps } from '../types';
import { AppBar } from '../components/AppBar';
import { BlockedCameraCard } from '../components/BlockedCameraCard';
import { Stepper } from '../components/Stepper';
import { Button } from '../components/Button';
import { Icon } from '../components/Icon';

/**
 * SCREEN 03 · CAPTURE — real self-capture (S7-6): a live document photo (rear
 * camera) then a selfie (front camera), each through the on-device quality
 * gate that BLOCKS a bad capture with the named reason (UX_SPEC §3.4 safety
 * condition 4), then uploaded to the session's server-side evidence store.
 * Both artefacts present → queue:enter → the waiting room.
 *
 * Honesty rules encoded here:
 *   • The tile claim is exactly "Quality-checked on device" — NEVER "verified"
 *     (verification = server-side pre-join checks + the live officer; claiming
 *     it here would be a compliance violation).
 *   • The sha256 shown is the SERVER's hash of what it stored (integrity cue).
 *   • Resume renders already-captured kinds from server METADATA only — the
 *     bytes are never re-downloadable, so a neutral done-tile, not a thumbnail.
 *   • 409 on upload = that kind is already bound (write-once): treated as
 *     success, OUR local bytes are dropped (we cannot claim they're the bound
 *     ones) and the list is refreshed.
 *   • 422 CONSENT_REQUIRED routes BACK to the consent step — capture must
 *     follow recorded consent (DPDP ordering).
 *
 * PII discipline: no image data in logs, no blobs in storage; object URLs are
 * revoked when replaced and on unmount. Mock mode (no backend) keeps the
 * clearly-simulated illustration path for the design-system demo.
 */
export function DocumentCapture(props: StepProps): JSX.Element {
  // Mock vs real is env-fixed for the app's lifetime, so branching to two
  // dedicated components (each with its own hooks) is hook-rule safe.
  return isMockMode() ? <MockCapture {...props} /> : <RealCapture {...props} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Real capture flow
// ─────────────────────────────────────────────────────────────────────────────

/** The legacy self-capture pair this step owns (SELFIE_WITH_DOC_FRAME belongs
 *  to the plan-driven SelfieWithDocStep, never to this legacy fail-safe flow). */
type LegacyCaptureKind = 'DOC_PHOTO' | 'SELFIE_FRAME';

const CAPTURE_ORDER: readonly LegacyCaptureKind[] = ['DOC_PHOTO', 'SELFIE_FRAME'];

const KIND_META: Record<
  LegacyCaptureKind,
  { labelKey: string; titleKey: string; subKey: string; facing: CaptureFacing }
> = {
  DOC_PHOTO: {
    labelKey: 'droom.capture.doc.label',
    titleKey: 'droom.capture.doc.title',
    subKey: 'droom.capture.doc.sub',
    facing: 'environment',
  },
  SELFIE_FRAME: {
    labelKey: 'droom.capture.selfie.label',
    titleKey: 'droom.capture.selfie.title',
    subKey: 'droom.capture.selfie.sub',
    facing: 'user',
  },
};

type StepPhase =
  | 'loading' // listArtifacts on mount (resume support)
  | 'load-error'
  | 'capture' // the active sub-capture (camera machine + shot preview)
  | 'entering-queue'
  | 'queue-error'
  | 'consent-required'; // server says no consent record — route back

interface DoneArtifact {
  meta: ArtifactMeta;
  /** Local preview URL when captured this visit; null on resume (metadata only). */
  previewUrl: string | null;
}

interface Shot {
  blob: Blob;
  url: string;
  verdict: QualityVerdict;
}

const IMG_FILL: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};
/** Centered large card guide for the full-document capture (positions only —
 *  the guide colours come from the .vp-guide-card class). */
const DOC_GUIDE: CSSProperties = {
  left: '7%',
  right: '7%',
  top: '18%',
  bottom: '34%',
  width: 'auto',
  height: 'auto',
};
/** Centered face guide for the plain selfie (vs the composite hero default). */
const FACE_GUIDE: CSSProperties = { top: '44%', width: 140, height: 178 };

function RealCapture({ tenant }: StepProps): JSX.Element {
  const { t } = useT();
  const sessionId = useSessionStore((s) => s.session.sessionId) as string;
  const setCaptureOk = useSessionStore((s) => s.setCaptureQualityOk);
  const advance = useSessionStore((s) => s.completeCurrentAndAdvance);
  const goToStep = useSessionStore((s) => s.goToStep);
  const setConsentAffirmed = useSessionStore((s) => s.setConsentAffirmed);

  const cam = useCaptureCamera(true);
  /**
   * The customer's own lens choice for the CURRENT document sub-capture (item 3, 2026-08-24). Null =
   * the artefact's sensible default from KIND_META. Cleared whenever the sub-capture changes, so the
   * next artefact starts from its own default rather than inheriting the previous one's override.
   */
  const [facingOverride, setFacingOverride] = useState<CaptureFacing | null>(null);

  const [phase, setPhase] = useState<StepPhase>('loading');
  const [done, setDone] = useState<Partial<Record<LegacyCaptureKind, DoneArtifact>>>({});
  const [shot, setShot] = useState<Shot | null>(null);
  const [assessing, setAssessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [captureNote, setCaptureNote] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  const disposed = useRef(false);
  const urlsRef = useRef<string[]>([]);
  const queueFired = useRef(false);

  const activeKind: LegacyCaptureKind | null =
    CAPTURE_ORDER.find((k) => !done[k]) ?? null;

  // The lens actually in force: the customer's override for THIS sub-capture, else its default.
  const effectiveFacing: CaptureFacing | null =
    activeKind === null ? null : (facingOverride ?? KIND_META[activeKind].facing);

  // A lens override belongs to ONE sub-capture — moving on (doc → selfie) drops it, so the selfie
  // always starts front-facing regardless of what the document needed.
  useEffect(() => {
    setFacingOverride(null);
  }, [activeKind]);

  // ── item 7 (pane title): the PINNED docType names the document ──────────────
  // The session's pinned document requirements say WHICH document this tenant
  // wants ("Photograph your PAN card"), so the pane stops saying the generic
  // "ID document" when the plan is specific. Best-effort read: any failure or
  // an empty/selfie-only pin keeps today's generic title (older be, fail-soft).
  const [pinnedDocType, setPinnedDocType] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    listDocumentRequirements(sessionId)
      .then((rows) => {
        if (!active) return;
        const docs = rows.filter(
          (r) => r.docType !== 'SELFIE' && r.docType !== 'SELFIE_WITH_DOC',
        );
        const pick = docs.find((r) => r.requirement === 'REQUIRED') ?? docs[0];
        if (pick) setPinnedDocType(pick.docType);
      })
      .catch(() => {
        /* fail-soft: the generic title is the honest default */
      });
    return () => {
      active = false;
    };
  }, [sessionId]);
  // Localised document label: known vocabulary via the deck; unknown docTypes
  // keep the humanised EN fallback (deploy-skew names are not translatable).
  const pinnedDocLabel = ((): string | null => {
    if (!pinnedDocType) return null;
    const key = `droom.doctype.${pinnedDocType}`;
    const translated = t(key);
    return translated === key ? docTypeLabel(pinnedDocType) : translated;
  })();

  // Deepfake layer-2 capture-integrity (injection / virtual-camera) fingerprint (S5-4; AI_DESIGN §0.2).
  // Collected off the customer's LIVE SELFIE capture — the face surface an injected virtual camera would
  // feed — and posted best-effort. Evidence for the officer's panel, never a gate on the customer;
  // probabilistic, client-reported, NOT proof of camera provenance (attested capture is the roadmap).
  useCaptureIntegrity(
    sessionId,
    cam.getStream,
    activeKind === 'SELFIE_FRAME' && cam.phase === 'live',
  );

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
      // Release every preview object URL — never leave image data reachable.
      for (const u of urlsRef.current) URL.revokeObjectURL(u);
      urlsRef.current = [];
    };
  }, []);

  // ── resume: the server list is the source of truth for what's captured ──────
  const refreshList = useCallback(async () => {
    setPhase('loading');
    setErrorDetail(null);
    try {
      const list = await listArtifacts(sessionId);
      if (disposed.current) return;
      setDone((prev) => {
        const next: Partial<Record<LegacyCaptureKind, DoneArtifact>> = { ...prev };
        for (const meta of list) {
          if (meta.kind === 'DOC_PHOTO' || meta.kind === 'SELFIE_FRAME') {
            next[meta.kind] = {
              meta,
              previewUrl: prev[meta.kind]?.previewUrl ?? null,
            };
          }
        }
        return next;
      });
      setPhase('capture');
    } catch (e) {
      if (disposed.current) return;
      setErrorDetail(
        e instanceof ApiError && e.status === 404
          ? 'droom.capture.err.link'
          : 'droom.capture.err.list',
      );
      setPhase('load-error');
    }
  }, [sessionId]);

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  // ── camera lifecycle: start/stop/switch driven by the active sub-capture ────
  useEffect(() => {
    if (phase !== 'capture' || activeKind === null) {
      cam.stop(); // leaving capture (queue/consent/loading) → release the camera
      return;
    }
    if (shot) return; // preview showing — keep the stream for an instant retake
    const facing = effectiveFacing ?? KIND_META[activeKind].facing;
    if (cam.phase === 'primer' && cam.preGranted) {
      cam.start(facing); // permission already granted — skip the primer tap
      return;
    }
    if (cam.phase === 'live' && cam.requestedFacing !== facing) {
      cam.start(facing); // sub-capture switch — stop-before-reacquire inside
    }
  }, [
    phase,
    activeKind,
    shot,
    effectiveFacing,
    cam.phase,
    cam.preGranted,
    cam.requestedFacing,
    cam.start,
    cam.stop,
  ]);

  // ── both artefacts bound → enter the verifier queue (idempotent) ────────────
  const doEnterQueue = useCallback(async () => {
    setPhase('entering-queue');
    setErrorDetail(null);
    try {
      await enterQueue(sessionId);
      if (disposed.current) return;
      setCaptureOk(true); // the gate passed: REAL evidence is bound server-side
      advance();
    } catch (e) {
      if (disposed.current) return;
      queueFired.current = false; // allow a retry
      if (
        e instanceof ApiError &&
        e.status === 422 &&
        e.message.includes(CONSENT_REQUIRED_MARKER)
      ) {
        setPhase('consent-required');
        return;
      }
      setErrorDetail(
        e instanceof ApiError && e.status === 422
          ? e.message // the contract: the 422 message names exactly what's missing
          : 'droom.capture.err.queue',
      );
      setPhase('queue-error');
    }
  }, [sessionId, setCaptureOk, advance]);

  useEffect(() => {
    if (phase !== 'capture' || activeKind !== null || queueFired.current) return;
    queueFired.current = true;
    void doEnterQueue();
  }, [phase, activeKind, doEnterQueue]);

  // ── capture → on-device quality gate → preview ──────────────────────────────
  const onCapture = async (): Promise<void> => {
    if (assessing || !activeKind) return;
    setAssessing(true);
    setCaptureNote(null);
    try {
      const blob = await cam.captureFrame();
      if (disposed.current) return;
      if (!blob) {
        setCaptureNote('droom.capture.camera.notready');
        return;
      }
      const verdict = await assessCaptureQuality(blob, activeKind);
      if (disposed.current) return;
      const url = URL.createObjectURL(blob);
      urlsRef.current.push(url);
      setUploadNote(null);
      setShot({ blob, url, verdict });
    } finally {
      if (!disposed.current) setAssessing(false);
    }
  };

  const onRetake = (): void => {
    if (shot) {
      URL.revokeObjectURL(shot.url);
      urlsRef.current = urlsRef.current.filter((u) => u !== shot.url);
    }
    setShot(null);
    setUploadNote(null);
  };

  // ── accept → upload to the evidence store ───────────────────────────────────
  const onAccept = async (): Promise<void> => {
    if (!shot || !activeKind || !shot.verdict.ok || uploading) return;
    const kind = activeKind;
    const current = shot;
    setUploading(true);
    setUploadNote(null);
    try {
      const meta = await uploadArtifact(sessionId, kind, current.blob);
      if (disposed.current) return;
      setDone((p) => ({ ...p, [kind]: { meta, previewUrl: current.url } }));
      setShot(null);
    } catch (e) {
      if (disposed.current) return;
      if (e instanceof ApiError && e.status === 409) {
        // Write-once: this kind is already bound server-side. Success path —
        // but drop OUR bytes (we cannot claim they're the bound ones) and let
        // the refreshed server list render the honest done-tile.
        URL.revokeObjectURL(current.url);
        urlsRef.current = urlsRef.current.filter((u) => u !== current.url);
        setShot(null);
        void refreshList();
        return;
      }
      if (e instanceof ApiError && e.status === 422) {
        if (e.message.includes(CONSENT_REQUIRED_MARKER)) {
          setPhase('consent-required');
          return;
        }
        setUploadNote(e.message); // capture window closed / bad file — server names it
        return;
      }
      setUploadNote('droom.capture.uploadfailed');
    } finally {
      if (!disposed.current) setUploading(false);
    }
  };

  const onReviewConsent = (): void => {
    setConsentAffirmed(false); // the server says no record exists — don't let the guard lie
    goToStep('consent');
  };

  const restartCamera = (): void => {
    if (activeKind) cam.start(effectiveFacing ?? KIND_META[activeKind].facing);
  };

  /**
   * Flip the lens DURING DOCUMENT CAPTURE (2026-08-24 owner round, item 3).
   *
   * <p>KIND_META picks a sensible default per artefact — rear for a document, front for a selfie —
   * but the default is not always right: laptops and many tablets have no rear camera at all, some
   * phones enumerate an ultra-wide as "environment" that cannot focus at document distance, and a
   * customer holding a passport up to a webcam needs the front lens. The default stays; the customer
   * now gets the last word.
   *
   * <p>Deliberately DOCUMENT-ONLY (the requirement says so, and it is right): the selfie sub-capture
   * keeps its front lens with no toggle, because a "selfie" taken on the rear camera pointed at a
   * photograph is precisely the presentation attack the liveness and face-match rails exist to
   * refuse. Offering the flip there would be offering a hole.
   */
  const flipDocumentLens = (): void => {
    if (activeKind !== 'DOC_PHOTO') return;
    const current = effectiveFacing ?? KIND_META.DOC_PHOTO.facing;
    setFacingOverride(current === 'environment' ? 'user' : 'environment');
  };

  const copyLink = (): void => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    void navigator.clipboard.writeText(window.location.href).then(() => {
      if (disposed.current) return;
      setLinkCopied(true);
      window.setTimeout(() => {
        if (!disposed.current) setLinkCopied(false);
      }, 2500);
    });
  };

  // ── consent-required: honest block + route back to the consent ceremony ─────
  if (phase === 'consent-required') {
    return (
      <div className="app-view">
        <AppBar tenant={tenant} subtitle={t('droom.capture.subtitle')} />
        <Stepper current="capture" />
        <div className="app-view center" style={{ paddingTop: 24 }}>
          <div className="center-col" style={{ maxWidth: '34ch' }}>
            <span className="consent-gate-badge">
              <Icon name="lock" size={28} />
            </span>
            <div className="h-title sm">{t('droom.consentreq.title')}</div>
            <p className="sub" style={{ marginTop: 8 }}>
              {t('droom.consentreq.body.docs')}
            </p>
            <Button style={{ marginTop: 18 }} onClick={onReviewConsent}>
              {t('droom.consentreq.review')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const camBlocked =
    cam.phase === 'denied' ||
    cam.phase === 'os-denied' ||
    cam.phase === 'no-device' ||
    cam.phase === 'webview' ||
    cam.phase === 'unsupported';

  // Header copy per phase.
  let title: string;
  let sub: string;
  if (phase === 'loading') {
    title = t('droom.capture.loading.title');
    sub = t('droom.capture.loading.sub');
  } else if (phase === 'load-error') {
    title = t('droom.capture.snag');
    sub = errorDetail ? t(errorDetail) : t('droom.error.tryAgain');
  } else if (phase === 'entering-queue') {
    title = t('droom.capture.complete');
    sub = t('droom.capture.queue.entering');
  } else if (phase === 'queue-error') {
    title = t('droom.capture.complete');
    sub = errorDetail ? t(errorDetail) : t('droom.capture.queue.failed');
  } else if (activeKind) {
    // The pinned docType names the document (item 7); generic otherwise.
    title =
      activeKind === 'DOC_PHOTO' && pinnedDocLabel
        ? t('droom.capture.doc.titleTyped', { doc: pinnedDocLabel })
        : t(KIND_META[activeKind].titleKey);
    sub = t(KIND_META[activeKind].subKey);
  } else {
    title = t('droom.capture.complete');
    sub = t('droom.capture.moment');
  }

  const mirrored = (cam.activeFacing ?? cam.requestedFacing) === 'user';
  const videoStyle: CSSProperties = mirrored
    ? { objectFit: 'cover', transform: 'scaleX(-1)' }
    : { objectFit: 'cover' };
  const facingShown = cam.activeFacing ?? cam.requestedFacing;
  const badgeLabel = shot
    ? t('droom.capture.badge.preview')
    : facingShown === 'environment'
      ? t('droom.capture.badge.rear')
      : facingShown === 'user'
        ? t('droom.capture.badge.front')
        : t('droom.capture.badge.camera');

  // Viewfinder hint line (the vp-hint pill).
  const hint = ((): { good: boolean; alert: boolean; text: string } => {
    if (shot) {
      if (!shot.verdict.ok) {
        return {
          good: false,
          alert: true,
          text: t(shot.verdict.messageKey ?? 'droom.capture.retakeThis'),
        };
      }
      if (uploading) return { good: false, alert: false, text: t('droom.capture.uploading') };
      return { good: true, alert: false, text: t('droom.capture.preview.ok') };
    }
    if (assessing) {
      return { good: false, alert: false, text: t('droom.capture.assessing') };
    }
    if (captureNote) return { good: false, alert: false, text: t(captureNote) };
    switch (cam.phase) {
      case 'checking':
        return { good: false, alert: false, text: t('droom.capture.camera.checking') };
      case 'primer':
        return {
          good: false,
          alert: false,
          text:
            activeKind === 'SELFIE_FRAME'
              ? t('droom.selfie.primer')
              : t('droom.capture.primer.rear'),
        };
      case 'starting':
        return { good: false, alert: false, text: t('droom.capture.camera.starting') };
      case 'live':
        return {
          good: false,
          alert: false,
          text:
            activeKind === 'SELFIE_FRAME'
              ? t('droom.capture.hint.face')
              : t('droom.capture.hint.doc'),
        };
      default:
        return { good: false, alert: false, text: '' };
    }
  })();

  // Main body: blocked-camera card, or the live viewport / captured preview.
  let body: JSX.Element;
  if (phase === 'loading' || phase === 'load-error') {
    body = (
      <div className="viewport wide" aria-hidden="true">
        <div className="vp-hint">
          {phase === 'loading' ? t('droom.capture.session.checking') : t('droom.capture.session.loadfail')}
        </div>
      </div>
    );
  } else if (camBlocked && activeKind) {
    body = <BlockedCameraCard cam={cam} />;
  } else {
    const showVideo = !shot && (cam.phase === 'starting' || cam.phase === 'live');
    body = (
      <div className="viewport wide">
        {shot ? (
          <img src={shot.url} alt={t('droom.capture.previewAlt')} style={IMG_FILL} />
        ) : showVideo ? (
          <video
            ref={cam.attachVideo}
            className="vp-scene"
            autoPlay
            playsInline
            muted
            style={videoStyle}
          />
        ) : null}

        <span className="vp-badge">
          <Icon name="camera" size={12} />
          {badgeLabel}
        </span>

        {!shot && cam.phase === 'live' && activeKind ? (
          <div className="vp-frame">
            {activeKind === 'DOC_PHOTO' ? (
              <div className="vp-guide-card" style={DOC_GUIDE} />
            ) : (
              <div className="vp-guide-face" style={FACE_GUIDE} />
            )}
          </div>
        ) : null}

        {hint.text ? (
          <div className="vp-hint" role={hint.alert ? 'alert' : 'status'}>
            {hint.good ? (
              <span className="g">
                <Icon name="check" size={15} />
              </span>
            ) : null}
            {hint.text}
          </div>
        ) : null}
      </div>
    );
  }

  // Primary action(s) — one brand-filled primary per state (Hick's Law).
  let actions: JSX.Element;
  if (phase === 'loading') {
    actions = <Button disabled>{t('droom.action.checking')}</Button>;
  } else if (phase === 'load-error') {
    actions = <Button onClick={() => void refreshList()}>{t('droom.action.tryAgain')}</Button>;
  } else if (phase === 'entering-queue') {
    actions = <Button disabled>{t('droom.capture.queue.enteringBtn')}</Button>;
  } else if (phase === 'queue-error') {
    actions = <Button onClick={() => void doEnterQueue()}>{t('droom.action.tryAgain')}</Button>;
  } else if (camBlocked) {
    actions =
      cam.phase === 'webview' || cam.phase === 'unsupported' ? (
        <Button onClick={copyLink}>
          {linkCopied ? t('droom.action.linkCopied') : t('droom.action.copyLink')}
        </Button>
      ) : (
        <Button onClick={restartCamera}>
          {cam.phase === 'no-device' ? t('droom.action.tryAgain') : t('droom.action.allowed')}
        </Button>
      );
  } else if (shot) {
    actions = !shot.verdict.ok ? (
      <Button onClick={onRetake}>{t('droom.action.retake')}</Button>
    ) : uploading ? (
      <Button disabled>{t('droom.capture.uploading')}</Button>
    ) : (
      <>
        <Button onClick={() => void onAccept()}>{t('droom.capture.use')}</Button>
        <Button variant="ghost" onClick={onRetake}>
          {t('droom.action.retake')}
        </Button>
      </>
    );
  } else {
    switch (cam.phase) {
      case 'primer':
        actions = <Button onClick={restartCamera}>{t('droom.capture.turnon')}</Button>;
        break;
      case 'live':
        actions = (
          <>
            <Button onClick={() => void onCapture()} disabled={assessing}>
              {assessing ? t('droom.capture.assessing.short') : t('droom.capture.shoot')}
            </Button>
            {/* Item 3 (2026-08-24): document sub-capture ONLY — never on the selfie (a rear-lens
                "selfie" pointed at a photo is the attack the liveness rail exists to refuse). */}
            {activeKind === 'DOC_PHOTO' ? (
              <Button variant="ghost" onClick={flipDocumentLens} disabled={assessing} type="button">
                {t('droom.capture.flip')}
              </Button>
            ) : null}
          </>
        );
        break;
      case 'starting':
        actions = <Button disabled>{t('droom.capture.camera.startingBtn')}</Button>;
        break;
      default:
        actions = <Button disabled>{t('droom.capture.camera.checkingBtn')}</Button>;
    }
  }

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.capture.subtitle')} />
      <Stepper current="capture" />

      <div className="stack">
        <div>
          <div className="h-title sm">{title}</div>
          <p className="sub-sm" style={{ marginTop: 6 }}>
            {sub}
          </p>
        </div>

        {body}

        {uploadNote ? (
          <div className="consent-note mono" role="alert">
            {t(uploadNote)}
          </div>
        ) : null}

        <div className="captured-strip">
          {CAPTURE_ORDER.map((kind) => (
            <ArtifactTile
              key={kind}
              kind={kind}
              done={done[kind] ?? null}
              active={phase === 'capture' && activeKind === kind}
              uploading={uploading && activeKind === kind}
              labelOverride={kind === 'DOC_PHOTO' ? pinnedDocLabel : null}
            />
          ))}
        </div>
      </div>

      <div className="spacer" />

      <div className="stack-sm" style={{ marginTop: 14 }}>
        {actions}
        <div className="cobrand" style={{ fontSize: 11 }}>
          {t('droom.capture.footer')}
        </div>
      </div>
    </div>
  );
}

/** One artefact tile in the captured strip. Never claims "verified". */
function ArtifactTile({
  kind,
  done,
  active,
  uploading,
  labelOverride = null,
}: {
  kind: LegacyCaptureKind;
  done: DoneArtifact | null;
  active: boolean;
  uploading: boolean;
  /** The pinned docType's label (item 7) — null keeps the generic tile label. */
  labelOverride?: string | null;
}): JSX.Element {
  const { t } = useT();
  const thumbBase: CSSProperties = {
    width: 40,
    height: 28,
    borderRadius: 5,
    flex: 'none',
    overflow: 'hidden',
  };
  return (
    <div className="captured">
      {done?.previewUrl ? (
        <span style={{ ...thumbBase, position: 'relative' }}>
          <img src={done.previewUrl} alt="" aria-hidden="true" style={IMG_FILL} />
        </span>
      ) : (
        <span
          style={{
            ...thumbBase,
            display: 'grid',
            placeItems: 'center',
            background: done ? 'var(--success-subtle)' : 'var(--surface-elevated)',
            color: done ? 'var(--success)' : 'var(--text-muted)',
          }}
        >
          <Icon name={kind === 'DOC_PHOTO' ? 'doc' : 'face'} size={14} />
        </span>
      )}
      <div style={{ minWidth: 0 }}>
        <div className="cn">{labelOverride ?? t(KIND_META[kind].labelKey)}</div>
        {done ? (
          <>
            <div className="cs">
              <Icon name="check" size={11} />
              {done.previewUrl ? t('droom.capture.grade.live') : t('droom.capture.tile.uploadedEarlier')}
            </div>
            <div
              className="mono"
              title={t('droom.capture.tile.hash')}
              style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.03em' }}
            >
              {done.meta.sha256.slice(0, 12)}
            </div>
          </>
        ) : (
          <div
            className="cs"
            style={{ color: active ? 'var(--brand)' : 'var(--text-muted)' }}
          >
            {uploading ? t('droom.capture.tile.uploading') : active ? t('droom.capture.tile.inprogress') : t('droom.capture.tile.next')}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Mock/demo mode — the clearly-simulated skeleton path (design-system demo)
// ─────────────────────────────────────────────────────────────────────────────

function MockCapture({ tenant }: StepProps): JSX.Element {
  const setCaptureOk = useSessionStore((s) => s.setCaptureQualityOk);
  const advance = useSessionStore((s) => s.completeCurrentAndAdvance);

  // Demo only: a real backend build runs the camera + quality gate + upload
  // (RealCapture above); here Capture marks the gate passed then advances.
  const onCapture = () => {
    setCaptureOk(true);
    advance();
  };

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle="Step 2 · Capture" />
      <Stepper current="capture" />

      <div className="stack">
        <div>
          <div className="h-title sm">Hold your ID beside your face</div>
          <p className="sub-sm" style={{ marginTop: 6 }}>
            One frame proves the document and the person match. Center both inside
            the guides.
          </p>
        </div>

        {/* Selfie-holding-document viewport (illustration — demo mode only) */}
        <div
          className="viewport wide"
          role="img"
          aria-label="Camera guide: hold your identity card next to your face inside the on-screen outlines"
        >
          <svg
            className="vp-scene"
            viewBox="0 0 300 340"
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="cam" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#26323f" />
                <stop offset="1" stopColor="#141d27" />
              </linearGradient>
            </defs>
            <rect width="300" height="340" fill="url(#cam)" />
            <path d="M60 340 C60 262 108 232 150 232 C192 232 240 262 240 340 Z" fill="#3c4a5a" />
            <circle cx="150" cy="150" r="52" fill="#c99f86" />
            <path d="M104 138 C104 104 196 104 196 138 L196 120 C196 92 104 92 104 120 Z" fill="#2f2a2c" />
            <circle cx="132" cy="150" r="5" fill="#2f2a2c" />
            <circle cx="168" cy="150" r="5" fill="#2f2a2c" />
            <path d="M138 176 q12 9 24 0" stroke="#8a5a45" strokeWidth="3" fill="none" strokeLinecap="round" />
            <g transform="rotate(-8 232 232)">
              <rect x="196" y="206" width="86" height="56" rx="7" fill="#eef3f8" stroke="#cdd8e5" strokeWidth="1.5" />
              <circle cx="214" cy="228" r="10" fill="#b9c8db" />
              <rect x="230" y="219" width="42" height="5" rx="2.5" fill="#b9c8db" />
              <rect x="230" y="230" width="34" height="4" rx="2" fill="#cdd8e5" />
              <rect x="206" y="246" width="66" height="4" rx="2" fill="#cdd8e5" />
            </g>
            <path d="M196 232 q18 -4 30 -18" stroke="#c99f86" strokeWidth="16" fill="none" strokeLinecap="round" />
          </svg>

          <span className="vp-badge">
            <Icon name="camera" size={12} />
            DEMO · SIMULATED
          </span>
          <div className="vp-frame">
            <div className="vp-guide-face" />
            <div className="vp-guide-card" />
          </div>
          <div className="vp-hint">
            <span className="g">
              <Icon name="check" size={15} />
            </span>
            Face &amp; ID detected — hold steady
          </div>
        </div>

        {/* Already-captured documents (quality-gated) */}
        <div className="captured-strip">
          <div className="captured">
            <div className="thumb" />
            <div>
              <div className="cn">PAN</div>
              <div className="cs">
                <Icon name="check" size={11} />
                Looks good
              </div>
            </div>
          </div>
          <div className="captured">
            <div className="thumb" />
            <div>
              <div className="cn">Aadhaar</div>
              <div className="cs">
                <Icon name="check" size={11} />
                Looks good
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="spacer" />

      <div className="stack-sm" style={{ marginTop: 14 }}>
        <Button onClick={onCapture}>Capture</Button>
        <div className="cobrand" style={{ fontSize: 11 }}>
          Quality is checked on-device before you continue
        </div>
      </div>
    </div>
  );
}
